/**
 * @typedef {import('../shared/BlogService/BlogPostExcerpt').default} BlogPostExcerpt
 * @typedef {{ tagId: string; tagName: string; slug: string }} BlogTag
 * @typedef {{ total?: number; results?: SiteSearchResult[] }} SiteSearchResponse
 */

/**
 * @typedef {{
 *   authorFullName?: string;
 *   description?: string;
 *   featuredImageUrl?: string;
 *   publishedDate?: string | number;
 *   tags?: unknown[];
 *   title?: string;
 *   url?: string;
 * }} SiteSearchResult
 */

import { translate } from '../App/Internationalization';

/**
 * @param {string | undefined} value
 * @returns {string}
 */
function stripHtml(value) {
	return (value ?? '').replace(/<[^>]+>/g, '').trim();
}

/**
 * @param {string | undefined} url
 * @returns {string}
 */
function getSlugFromUrl(url) {
	if (!url) {
		return '';
	}

	try {
		const pathname = new URL(url).pathname;
		return pathname.split('/').filter(Boolean).pop() ?? '';
	} catch (error) {
		return url.split('/').filter(Boolean).pop() ?? '';
	}
}

/**
 * @param {unknown} tag
 * @param {Map<string, number>} tagIdByName
 * @returns {number | undefined}
 */
function normalizeTagId(tag, tagIdByName) {
	if (typeof tag === 'number' && Number.isFinite(tag)) {
		return tag;
	}

	if (typeof tag !== 'string') {
		return undefined;
	}

	const tagIdByTagName = tagIdByName.get(tag.trim().toLowerCase());
	if (tagIdByTagName !== undefined) {
		return tagIdByTagName;
	}

	const tagIdFromString = Number(tag);
	return Number.isNaN(tagIdFromString) ? undefined : tagIdFromString;
}

/**
 * @param {unknown[] | undefined} tags
 * @param {Map<string, number>} tagIdByName
 * @returns {number[]}
 */
function normalizeTagIds(tags, tagIdByName) {
	const normalizedTagIds = new Set();

	for (const tag of tags ?? []) {
		const normalizedTagId = normalizeTagId(tag, tagIdByName);
		if (normalizedTagId !== undefined) {
			normalizedTagIds.add(normalizedTagId);
		}
	}

	return [...normalizedTagIds];
}

/**
 * @param {BlogTag[]} tags
 * @returns {Map<string, number>}
 */
export function buildTagIdByName(tags) {
	const tagIdByName = new Map();

	for (const tag of tags) {
		tagIdByName.set(tag.tagName.trim().toLowerCase(), Number(tag.tagId));
	}

	return tagIdByName;
}

/**
 * @param {BlogPostExcerpt} post
 * @param {Map<string, number>} tagIdByName
 * @returns {BlogPostExcerpt}
 */
export function normalizePostExcerpt(post, tagIdByName) {
	return {
		...post,
		content: stripHtml(post.content),
		tagIds: normalizeTagIds(post.tagIds, tagIdByName),
		title: stripHtml(post.title),
	};
}

/**
 * @param {SiteSearchResult} searchResult
 * @param {{
 *  tagIdByName: Map<string, number>;
 *  defaultAuthorImage: string;
 *  defaultImageUrl: string;
 * }} options
 * @returns {BlogPostExcerpt}
 */
export function mapSearchResultToPostExcerpt(searchResult, options) {
	return {
		author: {
			headshotUrl: options.defaultAuthorImage,
			name: searchResult.authorFullName ?? '',
		},
		content: stripHtml(searchResult.description),
		date: new Date(searchResult.publishedDate ?? Date.now()),
		imageUrl: searchResult.featuredImageUrl || options.defaultImageUrl,
		slug: getSlugFromUrl(searchResult.url),
		tagIds: normalizeTagIds(searchResult.tags, options.tagIdByName),
		title: stripHtml(searchResult.title),
	};
}

/**
 * @param {SiteSearchResult[]} searchResults
 * @param {{
 *  tagIdByName: Map<string, number>;
 *  defaultAuthorImage: string;
 *  defaultImageUrl: string;
 * }} options
 * @returns {BlogPostExcerpt[]}
 */
export function mapSearchResultsToPostExcerpts(searchResults, options) {
	return searchResults.map((searchResult) =>
		mapSearchResultToPostExcerpt(searchResult, options)
	);
}

/**
 * @param {{
 *  searchUrl: string;
 *  query: string;
 *  limit: number;
 *  offset: number;
 * }} options
 * @returns {Promise<SiteSearchResponse>}
 */
export async function fetchSiteSearchPage(options) {
	const searchParamsBuilder = new URLSearchParams();
	searchParamsBuilder.append('q', options.query);
	searchParamsBuilder.append('limit', options.limit.toFixed(0));
	searchParamsBuilder.append('offset', options.offset.toFixed(0));
	const response = await fetch(
		`${options.searchUrl}?${searchParamsBuilder.toString()}`
	);

	if (!response.ok) {
		throw new Error(translate('agent.pages.blogs.error'));
	}

	return response.json();
}
